import { db } from '@/lib/firebase';
import { 
  collection, 
  addDoc, 
  getDocs, 
  getDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  where,
  orderBy,
  Timestamp 
} from 'firebase/firestore';

const BLOG_COLLECTION = 'blog_posts';

export interface BlogPost {
  id?: string;
  title: string;
  content: string;
  image: string;
  author: string;
  category: string;
  tags: string[];
  createdAt: Date;
  updatedAt: Date;
}

// إنشاء مقال جديد
export const createBlogPost = async (postData: Omit<BlogPost, 'id' | 'createdAt' | 'updatedAt'>) => {
  try {
    console.log('Creating new blog post:', postData);
    const postsRef = collection(db, BLOG_COLLECTION);
    const docRef = await addDoc(postsRef, {
      ...postData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    console.log('Blog post created successfully with ID:', docRef.id);
    return { id: docRef.id, ...postData };
  } catch (error) {
    console.error('Error creating blog post:', error);
    throw error;
  }
};

// الحصول على جميع المقالات
export const getAllBlogPosts = async () => {
  try {
    console.log('Fetching all blog posts...');
    const postsRef = collection(db, BLOG_COLLECTION);
    const q = query(postsRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    const posts = querySnapshot.docs.map(doc => {
      const data = doc.data();
      // Safely handle timestamp fields
      const createdAt = data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date();
      const updatedAt = data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : new Date();
      return {
        id: doc.id,
        ...data,
        createdAt: createdAt,
        updatedAt: updatedAt
      };
    });
    console.log(`Successfully fetched ${posts.length} blog posts`);
    return posts;
  } catch (error) {
    console.error('Error fetching blog posts:', error);
    throw error;
  }
};

// الحصول على مقال محدد
export const getBlogPost = async (id: string) => {
  try {
    console.log('Fetching blog post with ID:', id);
    const postRef = doc(db, BLOG_COLLECTION, id);
    const postSnap = await getDoc(postRef);
    
    if (!postSnap.exists()) {
      console.log('Blog post not found');
      return null;
    }

    const data = postSnap.data();
    // Safely handle timestamp fields
    const createdAt = data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date();
    const updatedAt = data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : new Date();

    console.log('Blog post found:', postSnap.id);
    return {
      id: postSnap.id,
      ...data,
      createdAt: createdAt,
      updatedAt: updatedAt
    };
  } catch (error) {
    console.error('Error fetching blog post:', error);
    throw error;
  }
};

// تحديث مقال
export const updateBlogPost = async (id: string, postData: Partial<BlogPost>) => {
  try {
    console.log('Updating blog post:', id, postData);
    const postRef = doc(db, BLOG_COLLECTION, id);
    await updateDoc(postRef, {
      ...postData,
      updatedAt: Timestamp.now()
    });
    console.log('Blog post updated successfully');
    return { id: id, ...postData };
  } catch (error) {
    console.error('Error updating blog post:', error);
    throw error;
  }
};

// حذف مقال
export const deleteBlogPost = async (id: string) => {
  try {
    console.log('Deleting blog post:', id);
    const postRef = doc(db, BLOG_COLLECTION, id);
    await deleteDoc(postRef);
    console.log('Blog post deleted successfully');
    return { id: id };
  } catch (error) {
    console.error('Error deleting blog post:', error);
    throw error;
  }
};

// الحصول على المقالات حسب الفئة
export const getBlogPostsByCategory = async (category: string) => {
  try {
    console.log('Fetching blog posts by category:', category);
    const q = query(
      collection(db, BLOG_COLLECTION),
      where('category', '==', category),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    const posts = querySnapshot.docs.map(doc => {
      const data = doc.data();
      // Safely handle timestamp fields
      const createdAt = data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date();
      const updatedAt = data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : new Date();
      return {
        id: doc.id,
        ...data,
        createdAt: createdAt,
        updatedAt: updatedAt
      };
    });
    console.log(`Successfully fetched ${posts.length} blog posts in category ${category}`);
    return posts;
  } catch (error) {
    console.error('Error getting blog posts by category:', error);
    throw error;
  }
}; 